<template>
  <p-product-detail-block
    v-if="hasToxicities || hasBiocideFamily"
    data-test="security-recommendations"
    :title="productName || i18n._('Recommandations réglementaires et sécurité')"
    :is-block-pack="isBlockPack"
  >
    <div>
      <div v-if="hasToxicities" class="row">
        <div
          v-for="toxicity in firstTwoToxicitiesWithoutPicto"
          :key="toxicity.id"
          data-test="first-toxicity-without-picto"
          class="security-recommendations__block col-md-6"
        >
          <div class="security-recommendations__title">{{ toxicity.id }}</div>
          <div>{{ toxicity.name }}</div>
        </div>
      </div>

      <div v-else-if="hasBiocideFamily" class="row">
        <div v-for="biocide in firstTwoBiocides" :key="biocide.title" class="security-recommendations__block col-md-6">
          <div class="security-recommendations__title">{{ biocide.title }}</div>
          <div>
            <div v-for="(item, idx) in biocide.items" :key="`${biocide.title}-${idx}`">{{ item.label }}.</div>
          </div>
        </div>
      </div>

      <div v-if="hasExpandableContent">
        <div
          v-show="!expandedContent"
          id="btn-expand"
          class="security-recommendations__toggle-button security-recommendations__toggle-button--expanded"
          data-test="expand-icon"
          @click="expandedContent = !expandedContent"
        >
          <span class="security-recommendations__toggle-button-text">{{ i18n._('Voir plus') }}</span>
          <span
            class="adn-icon adn-icon--navigation-direction-down security-recommendations__icon"
            aria-hidden="true"
          />
        </div>
        <transition v-show="expandedContent" name="security-recommendations__fade">
          <div v-show="expandedContent" id="security-recommendations__picto--details">
            <div class="row">
              <!-- Toxicités expandables -->
              <div
                v-for="toxicity in lastToxicitiesWithoutPicto"
                v-show="expandedContent"
                :key="toxicity.id"
                data-test="last-toxicity-without-picto"
                class="security-recommendations__block col-md-6"
              >
                <div class="security-recommendations__title">{{ toxicity.id }}</div>
                <div>{{ toxicity.name }}</div>
              </div>

              <!-- Biocides expandables -->
              <template v-if="hasBiocideFamily">
                <div
                  v-for="biocide in expandableBiocides"
                  v-show="expandedContent"
                  :key="biocide.title"
                  class="security-recommendations__block col-md-6"
                >
                  <div class="security-recommendations__title">{{ biocide.title }}</div>
                  <div>
                    <div v-for="(item, idx) in biocide.items" :key="`${biocide.title}-${idx}`">{{ item.label }}.</div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </transition>
        <div
          v-show="expandedContent"
          id="btn-collapse"
          data-test="collapse-icon"
          class="security-recommendations__toggle-button security-recommendations__toggle-button--expanded"
          @click="expandedContent = !expandedContent"
        >
          <span class="security-recommendations__toggle-button-text">{{ i18n._('Voir moins') }}</span>
          <span class="adn-icon adn-icon--navigation-direction-up security-recommendations__icon" aria-hidden="true" />
        </div>
      </div>
    </div>
  </p-product-detail-block>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref } from 'vue'
import { i18n } from '@b2ag/locale'
import PProductDetailBlock from '../PProductDetailBlock/PProductDetailBlock.vue'
import type { Toxicity } from './typings'
import type { BiocideFamilyApiResponse } from '@b2ag/module-product-sdk'

export default defineComponent({
  name: 'PProductDetailBlockSecurityRecommendations',
  components: {
    PProductDetailBlock,
  },
  props: {
    productName: {
      type: String,
      default: () => undefined,
    },
    toxicities: {
      type: Array as PropType<Toxicity[]>,
      default: () => [],
    },
    biocideFamilies: {
      type: Array as PropType<BiocideFamilyApiResponse[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { productName, toxicities, biocideFamilies } = props
    const expandedContent = ref(false)

    const firstTwoToxicitiesWithoutPicto = computed(() =>
      toxicities.filter((toxicity) => !toxicity.id.startsWith('SGH')).slice(0, 2),
    )
    const lastToxicitiesWithoutPicto = computed(() =>
      toxicities.filter((toxicity) => !toxicity.id.startsWith('SGH')).slice(2),
    )
    const isBlockPack = computed(() => productName !== undefined)
    const hasToxicities = computed(() => toxicities?.length > 0)
    const hasBiocideFamily = computed(() => biocideFamilies?.length > 0)

    const formattedBiocideFamilies = computed(() => {
      if (!biocideFamilies?.length) return []

      return biocideFamilies.flatMap((biocideObj) => {
        return Object.entries(biocideObj).map(([title, items]) => ({
          title,
          items,
        }))
      })
    })

    const firstTwoBiocides = computed(() => formattedBiocideFamilies.value.slice(0, 2))

    const expandableBiocides = computed(() =>
      hasToxicities.value ? formattedBiocideFamilies.value : formattedBiocideFamilies.value.slice(2),
    )

    const hasExpandableContent = computed(
      () =>
        lastToxicitiesWithoutPicto.value.length > 0 ||
        (hasBiocideFamily.value && (hasToxicities.value || formattedBiocideFamilies.value.length > 2)),
    )

    return {
      expandedContent,
      firstTwoToxicitiesWithoutPicto,
      lastToxicitiesWithoutPicto,
      isBlockPack,
      hasToxicities,
      hasBiocideFamily,
      hasExpandableContent,
      expandableBiocides,
      firstTwoBiocides,
      i18n,
    }
  },
})
</script>

<style scoped lang="scss">
.security-recommendations__toggle-button {
  display: inline-block;
  color: var(--color-main);
  font-weight: bold;
  cursor: pointer;

  &:hover {
    color: var(--color-main-dark);
  }
}

.security-recommendations__toggle-button--collapsed {
  margin-bottom: 1rem;
}

.security-recommendations__toggle-button--expanded {
  margin-top: 2rem;
}

.security-recommendations__toggle-button-text {
  margin-right: 1rem;
}

.security-recommendations__fade-enter-active,
.security-recommendations__fade-leave-active {
  transition: opacity 0.5s;
}

.security-recommendations__fade-enter, .security-recommendations__fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.security-recommendations__title {
  font-weight: bold;
  width: 100%;
}

@media (width <= 450px) {
  .security-recommendations__block {
    margin-top: 1rem;
  }
}

.security-recommendations__block {
  margin-bottom: 1rem;
}
</style>
